import React, { useState } from "react";
import { ArrowLeft, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { Input, Select } from "@/components/Field";
import { useAdminData, TableSkeletonRows } from "@/components/admin/useAdminData";

const STATUSES = ["open", "in_progress", "waiting_customer", "resolved", "closed"];

export default function Tickets() {
  const { t, sl } = useI18n();
  const { items: tickets, loading, reload: load } = useAdminData("/tickets");
  const [active, setActive] = useState(null);
  const [reply, setReply] = useState("");

  const open = async (id) => { const { data } = await api.get(`/tickets/${id}`); setActive(data); };
  const del = async (e, tk) => {
    e.stopPropagation();
    if (!window.confirm(`Ticket „${tk.number} – ${tk.subject}" wirklich dauerhaft löschen? Diese Aktion kann nicht rückgängig gemacht werden.`)) return;
    try { await api.delete(`/tickets/${tk.id}`); toast.success("Ticket gelöscht"); load(); }
    catch (err) { toast.error(apiErr(err)); }
  };
  const send = async (e) => {
    e.preventDefault();
    try { const { data } = await api.post(`/tickets/${active.id}/reply`, { body: reply }); setActive(data); setReply(""); load(); }
    catch (err) { toast.error(apiErr(err)); }
  };
  const setStatus = async (status) => { const { data } = await api.put(`/tickets/${active.id}`, { status }); setActive(data); load(); };

  if (active) return (
    <div className="max-w-2xl space-y-4">
      <button onClick={() => setActive(null)} className="inline-flex items-center gap-2 text-sm link-muted"><ArrowLeft size={16} /> {t("common.back")}</button>
      <div className="surface p-6">
        <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
          <h1 className="font-display text-xl font-black">{active.subject}</h1>
          <Select testid="admin-ticket-status" value={active.status} onChange={(e) => setStatus(e.target.value)}>{STATUSES.map((s) => <option key={s} value={s}>{sl(s)}</option>)}</Select>
        </div>
        <div className="text-xs text-muted-foreground mb-2">{active.number} · {active.customer_name} · {sl(active.priority)}</div>
        <p className="text-sm text-muted-foreground">{active.description}</p>
        <div className="mt-6 space-y-3">
          {(active.replies || []).map((r) => (
            <div key={r.id} className={`rounded-lg p-3 text-sm ${r.role === "staff" ? "bg-primary/10" : "bg-secondary"}`}>
              <div className="text-xs font-semibold mb-1">{r.author} · {r.role}</div>{r.body}
            </div>
          ))}
        </div>
        <form onSubmit={send} className="mt-4 flex gap-2">
          <Input testid="admin-ticket-reply" value={reply} onChange={(e) => setReply(e.target.value)} placeholder={t("portal.reply")} />
          <button className="btn-primary !px-4" data-testid="admin-ticket-send">{t("common.send")}</button>
        </form>
      </div>
    </div>
  );

  return (
    <div className="space-y-5">
      <h1 className="font-display text-2xl font-black">{t("admin.tickets")}</h1>
      <div className="surface overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-secondary text-left text-muted-foreground"><tr><th className="p-4">Nr.</th><th className="p-4">Kunde</th><th className="p-4">{t("contact.subject")}</th><th className="p-4">Priorität</th><th className="p-4">{t("common.status")}</th></tr></thead>
          <tbody>
            {loading ? <TableSkeletonRows cols={5} /> : tickets.map((tk) => (
              <tr key={tk.id} onClick={() => open(tk.id)} className="border-t border-border cursor-pointer hover:bg-secondary" data-testid={`aticket-${tk.id}`}>
                <td className="p-4 font-mono">{tk.number}</td><td className="p-4">{tk.customer_name}</td><td className="p-4">{tk.subject}</td>
                <td className="p-4 capitalize">{sl(tk.priority)}</td>
                <td className="p-4"><div className="flex items-center justify-between gap-2"><span className="rounded-full bg-primary/15 text-primary px-2.5 py-0.5 text-xs font-semibold capitalize">{sl(tk.status)}</span><button onClick={(e) => del(e, tk)} className="btn-ghost !py-1.5 !px-2.5 text-xs text-red-500 hover:text-red-600" data-testid={`ticket-delete-${tk.id}`}><Trash2 size={13} /></button></div></td>
              </tr>
            ))}
            {!loading && tickets.length === 0 && <tr><td colSpan={5} className="p-8 text-center text-muted-foreground">{t("common.none")}</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
